import { jsPDF } from 'jspdf'
import html2canvas from 'html2canvas'
import { renderTemplate, TEMPLATE_NAMES } from '../templates/TemplateRenderer'
import type { CaseStudy, TextSizingConfig } from '../types/caseStudy'
import type { EditorLayout } from '../types/editor'

const API_BASE_URL = (typeof import.meta !== 'undefined' && import.meta.env)
  ? import.meta.env.VITE_API_BASE_URL || ''
  : ''

const convertSrcToDataUrl = async (src: string): Promise<string | null> => {
  if (!src) return null
  if (src.startsWith('data:')) return src
  try {
    let urlToFetch = src
    if (/^https?:\/\//i.test(src)) {
      try {
        const u = new URL(src)
        if (u.hostname.endsWith('s3.amazonaws.com') && !u.hostname.includes('.s3.us-')) {
          u.hostname = u.hostname.replace('s3.amazonaws.com', 's3.us-west-2.amazonaws.com')
        }
        urlToFetch = u.toString()
      } catch (_) {}
    }
    const response = await fetch(urlToFetch, {
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'omit'
    })
    if (!response.ok) return src
    const blob = await response.blob()
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  } catch (_) {
    return src
  }
}

const inlineExternalImagesInHtml = async (html: string): Promise<string> => {
  try {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')

    const imageElements = Array.from(doc.querySelectorAll('img'))

    const toDataUrl = (blob: Blob): Promise<string> => new Promise((resolve, reject) => {
      try {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result as string)
        reader.onerror = reject
        reader.readAsDataURL(blob)
      } catch (e) { reject(e) }
    })

    await Promise.all(imageElements.map(async (img) => {
      try {
        const src = img.getAttribute('src') || ''
        if (!src || src.startsWith('data:') || src.startsWith('blob:')) return

        let url = src
        if (/^https?:\/\//i.test(src)) {
          try {
            const u = new URL(src)
            if (u.hostname.endsWith('s3.amazonaws.com') && !u.hostname.includes('.s3.us-')) {
              u.hostname = u.hostname.replace('s3.amazonaws.com', 's3.us-west-2.amazonaws.com')
            }
            const cacheBuster = `_cb=${Date.now()}`
            u.search = u.search ? `${u.search}&${cacheBuster}` : `?${cacheBuster}`
            url = u.toString()
          } catch (_) {}
        }

        const res = await fetch(url, { mode: 'cors', cache: 'no-cache' })
        if (!res.ok) return
        const blob = await res.blob()
        const dataUrl = await toDataUrl(blob)
        img.setAttribute('src', dataUrl)
      } catch (_) {}
    }))

    return '<!DOCTYPE html>' + doc.documentElement.outerHTML
  } catch (_) {
    return html
  }
}

const injectUserImagesIntoData = async (caseStudy: CaseStudy): Promise<CaseStudy> => {
  const enhanced = { ...caseStudy } as any
  try {
    const partnerImage = localStorage.getItem('preview_partner_image')
    const partnerSizePct = parseInt(localStorage.getItem('htmlPreview_partnerImageSize') || '100', 10)
    const partnerOffsetX = parseInt(localStorage.getItem('htmlPreview_partnerImageOffsetX') || '0', 10)
    const partnerOffsetY = parseInt(localStorage.getItem('htmlPreview_partnerImageOffsetY') || '0', 10)
    if (partnerImage) {
      enhanced._partnerImage = await convertSrcToDataUrl(partnerImage)
      enhanced._partnerImageSize = isNaN(partnerSizePct) ? 100 : partnerSizePct
      enhanced._partnerImageOffsetX = isNaN(partnerOffsetX) ? 0 : partnerOffsetX
      enhanced._partnerImageOffsetY = isNaN(partnerOffsetY) ? 0 : partnerOffsetY
    }

    const logoLeft = localStorage.getItem('htmlPreview_logo_left')
    const logoCenter = localStorage.getItem('htmlPreview_logo_center')
    const logoRight = localStorage.getItem('htmlPreview_logo_right')
    if (logoLeft || logoCenter || logoRight) {
      enhanced._objectiveLogos = {
        left: logoLeft ? await convertSrcToDataUrl(logoLeft) : null,
        center: logoCenter ? await convertSrcToDataUrl(logoCenter) : null,
        right: logoRight ? await convertSrcToDataUrl(logoRight) : null
      }
    }

    const graphics1 = localStorage.getItem('htmlPreview_graphics_logo_1')
    const graphics2 = localStorage.getItem('htmlPreview_graphics_logo_2')
    const g1Size = parseInt(localStorage.getItem('htmlPreview_graphics_logo_1_size') || '100', 10)
    const g2Size = parseInt(localStorage.getItem('htmlPreview_graphics_logo_2_size') || '100', 10)
    const g1OffsetX = parseInt(localStorage.getItem('htmlPreview_graphics_logo_1_offsetX') || '0', 10)
    const g1OffsetY = parseInt(localStorage.getItem('htmlPreview_graphics_logo_1_offsetY') || '0', 10)
    const g2OffsetX = parseInt(localStorage.getItem('htmlPreview_graphics_logo_2_offsetX') || '0', 10)
    const g2OffsetY = parseInt(localStorage.getItem('htmlPreview_graphics_logo_2_offsetY') || '0', 10)
    if (graphics1 || graphics2) {
      enhanced._graphicsLogos = {
        logo1: graphics1 ? await convertSrcToDataUrl(graphics1) : null,
        logo2: graphics2 ? await convertSrcToDataUrl(graphics2) : null,
        logo1Size: isNaN(g1Size) ? 100 : g1Size,
        logo2Size: isNaN(g2Size) ? 100 : g2Size,
        logo1OffsetX: isNaN(g1OffsetX) ? 0 : g1OffsetX,
        logo1OffsetY: isNaN(g1OffsetY) ? 0 : g1OffsetY,
        logo2OffsetX: isNaN(g2OffsetX) ? 0 : g2OffsetX,
        logo2OffsetY: isNaN(g2OffsetY) ? 0 : g2OffsetY
      }
    }
  } catch (e) {
    console.error('Error pre-injecting images:', e)
  }
  return enhanced as CaseStudy
}

const triggerDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

const generatePdfViaApi = async (
  caseStudy: CaseStudy,
  textSizingConfig: TextSizingConfig,
  layout?: EditorLayout
) => {
  const enhancedCaseStudy = await injectUserImagesIntoData(caseStudy)

  const page1Result = renderTemplate(enhancedCaseStudy, TEMPLATE_NAMES.PAGE_1, textSizingConfig, layout)
  const page1Html = await inlineExternalImagesInHtml(page1Result.html)

  const page2Result = renderTemplate(enhancedCaseStudy, TEMPLATE_NAMES.PAGE_2, textSizingConfig, layout)
  const page2Html = await inlineExternalImagesInHtml(page2Result.html)

  const title = caseStudy?.title || 'Case Study'
  const filename = `${title.replace(/[^\w\s-]/g, '').replace(/\s+/g, '_')}.pdf`

  const token = localStorage.getItem('id_token')
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(`${API_BASE_URL}/pdf/generate`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ page1_html: page1Html, page2_html: page2Html, filename }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
    throw new Error(errorData.error || `PDF generation failed: ${response.status}`)
  }

  const blob = await response.blob()
  triggerDownload(blob, filename)
}

interface FrameResult {
  iframe: HTMLIFrameElement
  document: Document
}

const createIsolatedFrame = (html: string): Promise<FrameResult> => {
  return new Promise((resolve, reject) => {
    const iframe = document.createElement('iframe')
    iframe.style.position = 'fixed'
    iframe.style.left = '-9999px'
    iframe.style.top = '0'
    iframe.style.width = '816px'
    iframe.style.height = '1056px'
    iframe.style.border = 'none'
    iframe.style.zIndex = '-1000'
    iframe.setAttribute('scrolling', 'no')

    document.body.appendChild(iframe)

    iframe.onload = () => {
      try {
        const doc = iframe.contentDocument!
        doc.open()
        doc.write(html)
        doc.close()

        const waitForAssets = async () => {
          try {
            if (doc.fonts && doc.fonts.ready) {
              await doc.fonts.ready
            }
          } catch (_) {}

          const images = Array.from(doc.querySelectorAll('img'))
          if (images.length > 0) {
            await Promise.all(images.map((img) => {
              return new Promise<void>(resolveImg => {
                if (img.complete && img.naturalWidth > 0) return resolveImg()
                const timeout = setTimeout(() => resolveImg(), 5000)
                img.onload = () => { clearTimeout(timeout); resolveImg() }
                img.onerror = () => { clearTimeout(timeout); resolveImg() }
              })
            }))
          }

          const body = doc.body
          if (body) {
            body.style.padding = '0'
            body.style.margin = '0'
            body.style.display = 'flex'
            body.style.justifyContent = 'center'
            body.style.alignItems = 'flex-start'
            body.style.minHeight = '100vh'
          }

          await new Promise(resolve => setTimeout(resolve, 1000))
          resolve({ iframe, document: doc })
        }

        waitForAssets().catch(reject)
      } catch (error) {
        reject(error)
      }
    }

    iframe.onerror = reject
    iframe.src = 'about:blank'
  })
}

const generatePdfViaHtml2Canvas = async (
  caseStudy: CaseStudy,
  textSizingConfig: TextSizingConfig,
  layout?: EditorLayout
) => {
  let frame1: HTMLIFrameElement | undefined
  let frame2: HTMLIFrameElement | undefined

  try {
    const enhancedCaseStudy = await injectUserImagesIntoData(caseStudy)

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'in',
      format: [8.5, 11]
    })

    const page1Html = renderTemplate(enhancedCaseStudy, TEMPLATE_NAMES.PAGE_1, textSizingConfig, layout)
    const page1HtmlInlined = await inlineExternalImagesInHtml(page1Html.html)
    const { iframe: iframe1, document: doc1 } = await createIsolatedFrame(page1HtmlInlined)
    frame1 = iframe1

    const canvas1 = await html2canvas(doc1.querySelector('.container') || doc1.body, {
      width: 816,
      height: 1056,
      scale: 2,
      useCORS: true,
      allowTaint: false,
      backgroundColor: '#ffffff',
    })

    const imgData1 = canvas1.toDataURL('image/png')
    pdf.addImage(imgData1, 'PNG', 0, 0, 8.5, 11)

    const page2Html = renderTemplate(enhancedCaseStudy, TEMPLATE_NAMES.PAGE_2, textSizingConfig, layout)
    const page2HtmlInlined = await inlineExternalImagesInHtml(page2Html.html)
    const { iframe: iframe2, document: doc2 } = await createIsolatedFrame(page2HtmlInlined)
    frame2 = iframe2

    const canvas2 = await html2canvas(doc2.querySelector('.container') || doc2.body, {
      width: 816,
      height: 1056,
      scale: 2,
      useCORS: true,
      allowTaint: false,
      backgroundColor: '#ffffff',
    })

    pdf.addPage()
    const imgData2 = canvas2.toDataURL('image/png')
    pdf.addImage(imgData2, 'PNG', 0, 0, 8.5, 11)

    const title = caseStudy?.title || 'Case Study'
    const filename = `${title.replace(/[^\w\s-]/g, '').replace(/\s+/g, '_')}.pdf`
    pdf.save(filename)

  } finally {
    try { if (frame1?.parentNode) frame1.parentNode.removeChild(frame1) } catch (_) {}
    try { if (frame2?.parentNode) frame2.parentNode.removeChild(frame2) } catch (_) {}
  }
}

export const generateCaseStudyPdf = async (
  caseStudy: CaseStudy,
  textSizingConfig: TextSizingConfig = {} as TextSizingConfig,
  layout?: EditorLayout
) => {
  if (!caseStudy) {
    throw new Error('Missing case study data')
  }

  if (API_BASE_URL) {
    try {
      await generatePdfViaApi(caseStudy, textSizingConfig, layout)
      return
    } catch (apiError) {
      console.warn('Cloudflare PDF generation failed, falling back to html2canvas:', apiError)
    }
  }

  await generatePdfViaHtml2Canvas(caseStudy, textSizingConfig, layout)
}

export default { generateCaseStudyPdf }
